// ActivityDetailModal — modal de detalhe de uma execução. Correção UX v2 §5.5:
// réplica LITERAL do <dialog id="dlgActivity" class="wide"> do wireframe
// docs/RAIOS-UX-SIDEBAR-SPEC.html (aba 06 Atividade):
//
//   .dl-h  — ícone + "Atividade" • <span class="dem">id…</span> + copybtn + closex
//   .dl-b  — .mxgrid (Duração · Custo · Tokens · Status com dot)
//            .fld Quando (mono "início — fim (timezone)")
//            .fld Identificação (.metab: ID · Handler · Demanda · Trace · Sessão)
//            .fld Input / Output / Erro (.acpre; erro vazio = placeholder
//            "— sem erro nesta execução" com .acpre.err)
//   .dl-f  — botão ghost "Fechar"
//
// SEM "Execuções filhas" (decisão do wireframe; o drawer ActivityDetailSheet
// foi substituído). Classes: base W0 (wireframe.css .wf-modal*/.wf-metricgrid/
// .wf-dot/.wf-btn) + específicas §06 (wf-activity.css .wf-fld/.wf-metab/
// .wf-acpre/.wf-copybtn/.wf-dem/.wf-hsep). Radix Dialog mantém portal/foco.
//
// Dados: selectedActivity (shape leve do feed) + selectedActivityDetail
// (row completa via GET /v1/activity/:kind/:id — api.getActivityDetail,
// carregada pelo mc-store.openActivityDetail). demand_id / session_id /
// handler_name existem nas rows cruas por kind mas não no tipo ActivityDetail
// — lidos via accessor defensivo.

import '@/styles/wf-activity.css';
import * as React from 'react';
import { Activity as ActivityIcon, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { useMCStore } from '@/stores/mc-store';
import { cn } from '@/lib/utils';
import { CopyIdButton, DataBlock, Field, PreBlock } from './json-view';
import { rawText } from './json-view-utils';
import { formatCostStrict, formatDuration, formatTokens } from '@/lib/format';
import {
  type ActivityDetail,
  type ActivityStatus,
  formatAbsoluteTimestamp,
  sanitizeErrorMessage,
} from '@/lib/activity';

/* ── Vocabulário do wireframe (modal mostra "ok", não "Concluído") ─────────── */

const STATUS_SHORT: Record<ActivityStatus, string> = {
  running: 'em execução',
  completed: 'ok',
  failed: 'falha',
  timeout: 'timeout',
  cancelled: 'cancelado',
  partial: 'parcial',
};

// Tons da base W0 (.wf-dot--*): default muted; run=info, ok, wait=amber, bad.
const STATUS_DOT: Record<ActivityStatus, string | null> = {
  running: 'wf-dot--run',
  completed: 'wf-dot--ok',
  failed: 'wf-dot--bad',
  timeout: 'wf-dot--wait',
  partial: 'wf-dot--wait',
  cancelled: null,
};

/* ── Helpers ────────────────────────────────────────────────────────────────── */

/** Trunca UUID/ID longo no padrão do wireframe: `7f3a2b91…2b8`. */
function shortId(value: string): string {
  if (value.length <= 14) return value;
  return `${value.slice(0, 8)}…${value.slice(-3)}`;
}

/** Lê campo string da row crua do detalhe (campos fora do tipo ActivityDetail). */
function rawString(detail: ActivityDetail | null, key: string): string | null {
  if (!detail) return null;
  const v = (detail as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Métrica do topo (wireframe .mx — .mxv valor mono grande + .mxk label). */
function MetricBox({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="wf-metric">
      <div className="wf-metric-value">{value}</div>
      <div className="wf-metric-label">{label}</div>
    </div>
  );
}

/** Célula de Identificação (wireframe .meta — .mk chave + .mv valor+copybtn). */
function MetaCell({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: string | null;
  copyValue?: string | null;
}) {
  return (
    <div className="wf-meta">
      <div className="wf-mk">{label}</div>
      <div className="wf-mv">
        {copyValue && <CopyIdButton value={copyValue} label={`${label} completo`} />}
        <span className="truncate">{value ?? '—'}</span>
      </div>
    </div>
  );
}

/** Bloco <pre class="acpre"> do wireframe (Input/Output/Erro). */
/* ── Modal ──────────────────────────────────────────────────────────────────── */

export function ActivityDetailModal() {
  const {
    selectedActivity,
    selectedActivityDetail,
    selectedActivityDetailLoading,
    closeActivityDetail,
  } = useMCStore();

  const item = selectedActivity;
  const detail = selectedActivityDetail;
  const open = !!item;

  if (!item) return null;

  const duration = formatDuration(item.duration_ms);
  const cost = formatCostStrict(item.cost_usd);
  // Wireframe mostra UM total de tokens ("8.2k") — Σ input+output.
  const tokensTotal = (item.tokens_input ?? 0) + (item.tokens_output ?? 0);
  const tokens = formatTokens(tokensTotal) ?? '—';

  // Identificação — campos extras da row crua (variam por kind; ver header).
  const demandId = rawString(detail, 'demand_id');
  const sessionId = rawString(detail, 'session_id');
  const handlerName =
    item.kind === 'handler'
      ? item.actor_slug ?? rawString(detail, 'handler_name')
      : rawString(detail, 'handler_name');
  const handlerDisplay = handlerName
    ? item.target_slug
      ? `${handlerName} → ${item.target_slug}`
      : handlerName
    : null;

  // Input/Output — agent usa colunas próprias (input_prompt/output_result);
  // companion_activity_log não tem input/output: o payload do turno vive em
  // `details` (JSONB) → exibido como Output.
  const rawDetails = detail
    ? ((detail as unknown as Record<string, unknown>)['details'] ?? null)
    : null;
  const inputValue =
    item.kind === 'agent' ? detail?.input_prompt ?? detail?.input : detail?.input;
  const outputValue =
    item.kind === 'agent'
      ? detail?.output_result ?? detail?.output
      : detail?.output ?? (item.kind === 'companion' ? rawDetails : null);

  const errMsg = sanitizeErrorMessage(item.error_message ?? detail?.error ?? null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeActivityDetail()}>
      <DialogContent
        hideClose
        className="wf-modal wf-modal--wide max-h-[85vh] overflow-y-auto p-0 rounded-[12px]"
      >
        {/* Header (wireframe .dl-h) */}
        <div className="wf-modal-header">
          <ActivityIcon className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden="true" />
          <DialogTitle className="text-[1.02rem] font-bold text-white" style={{ fontFamily: 'var(--wf-display)' }}>
            Atividade
          </DialogTitle>
          <span className="wf-hsep" aria-hidden="true">
            •
          </span>
          <span className="wf-dem" title="ID completo da atividade">
            {shortId(item.id)}
          </span>
          <CopyIdButton value={item.id} label="ID completo da atividade" />
          <button
            type="button"
            className="wf-modal-close"
            onClick={closeActivityDetail}
            aria-label="Fechar"
          >
            <X className="ic" aria-hidden="true" />
          </button>
        </div>

        {/* Body (wireframe .dl-b) */}
        <div className="wf-modal-body">
          {/* Métricas (wireframe .mxgrid) */}
          <div className="wf-metricgrid">
            <MetricBox label="Duração" value={duration} />
            <MetricBox label="Custo" value={cost} />
            <MetricBox label="Tokens" value={tokens} />
            <MetricBox
              label="Status"
              value={
                <span className="inline-flex items-center gap-2">
                  <span
                    className={cn('wf-dot', STATUS_DOT[item.status])}
                    aria-hidden="true"
                  />
                  {STATUS_SHORT[item.status]}
                </span>
              }
            />
          </div>

          {/* Quando (wireframe .fld — mono .82rem) */}
          <Field label="Quando">
            <p style={{ fontFamily: 'var(--wf-mono)', fontSize: '.82rem' }}>
              {formatAbsoluteTimestamp(item.started_at)} —{' '}
              {item.ended_at ? formatAbsoluteTimestamp(item.ended_at) : 'em execução'}{' '}
              <span style={{ color: 'var(--wf-muted)' }}>({timezone})</span>
            </p>
          </Field>

          {/* Identificação (wireframe .metab) */}
          <Field label="Identificação">
            {selectedActivityDetailLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <div className="wf-metab">
                <MetaCell label="ID" value={shortId(item.id)} copyValue={item.id} />
                <MetaCell label="Handler" value={handlerDisplay} />
                <MetaCell
                  label="Demanda"
                  value={demandId ? shortId(demandId) : null}
                  copyValue={demandId}
                />
                <MetaCell
                  label="Trace"
                  value={item.trace_id ? shortId(item.trace_id) : null}
                  copyValue={item.trace_id}
                />
                <MetaCell
                  label="Sessão"
                  value={sessionId ? shortId(sessionId) : null}
                  copyValue={sessionId}
                />
              </div>
            )}
          </Field>

          {/* Input / Output / Erro — AGT-08: objetos viram árvore acordeão por
              nível; botão copiar (conteúdo INTEIRO) ao lado do label, fora da
              rolagem do bloco. */}
          <Field label="Input" copy={inputValue != null ? rawText(inputValue) : null}>
            {selectedActivityDetailLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <DataBlock value={inputValue ?? null} />
            )}
          </Field>

          <Field label="Output" copy={outputValue != null ? rawText(outputValue) : null}>
            {selectedActivityDetailLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <DataBlock value={outputValue ?? null} />
            )}
          </Field>

          {/* Erro — sempre visível; sem erro = placeholder .acpre.err do
              wireframe. AGT-09: erro com JSON embutido ("400 {…}") também
              ganha a árvore acordeão. */}
          <Field label="Erro" copy={errMsg || null}>
            {errMsg ? (
              <DataBlock value={errMsg} variant="danger" />
            ) : (
              <PreBlock value="— sem erro nesta execução" variant="err" />
            )}
          </Field>
        </div>

        {/* Footer (wireframe .dl-f) */}
        <div className="wf-modal-footer">
          <button type="button" className="wf-btn wf-btn-ghost" onClick={closeActivityDetail}>
            Fechar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
